'use client';

/**
 * components/blocks/main-banner-list-block/main-banner-slide-content.tsx
 *
 * Presentational content of a single banner slide (image + overlay + copy/CTA).
 * Extracted so it can be driven either directly by the carousel (baseline) or by
 * `<ExperienceSlot>` (personalized variant). It is intentionally "dumb": it owns
 * no carousel state and takes the swappable content fields plus stable context
 * (locale / prefix / active flag) as props. The outer positioning + touch
 * wrapper stays in the carousel so slide indexing, dots and autoplay are
 * untouched by personalization.
 */

import React from 'react';

import { generateCorrectSlug } from '../../../helpers/generate-correct-slug';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import LinkButton from '../../ui/link-button/link-button';
import styles from './main-banner-list-block.module.scss';
import type { NinetailedComponentProps } from '../experience-slot/experience-slot';

export interface MainBannerSlideContentProps extends NinetailedComponentProps {
  id?: string;
  title?: string;
  text?: string;
  overlay?: boolean;
  mediaField?: {
    jsonBynderAsset?: {
      originalUrl?: string;
      thumbnail?: string;
    };
    altText?: string;
  };
  readMoreButtonTitle?: string;
  link?: any;
  externalLink?: string;
  // Passthrough context (never overridden by a variant):
  isActive?: boolean;
  node_locale?: string;
  prefix?: string;
  urlSubDirectorydictionary?: any;
}

const MainBannerSlideContent = ({
  id,
  title,
  text,
  overlay,
  mediaField,
  readMoreButtonTitle,
  link,
  externalLink,
  isActive,
  node_locale = '',
  prefix = '',
  urlSubDirectorydictionary,
  ninetailed,
  ninetailedTrack,
}: MainBannerSlideContentProps) => {
  // Fire a custom Ninetailed `ctr` event on the actual CTA click-through. This
  // is additive to the SDK's native click tracking (`trackClicks`) — both fire.
  // `ninetailedTrack` is only injected on the SDK-active path, so on the inert
  // baseline path this handler is not attached and no event is emitted.
  const handleCtaClick = ninetailedTrack
    ? () =>
      ninetailedTrack('ctr', {
        component: 'main-banner',
        bannerId: id,
        isPersonalized: ninetailed?.isPersonalized ?? false,
        audienceId: ninetailed?.audience?.id,
      })
    : undefined;

  return (
    <>
      {mediaField &&
        mediaField?.jsonBynderAsset &&
        mediaField?.jsonBynderAsset?.originalUrl && (
          <div className="!absolute inset-0 h-screen lg:h-auto">
            <img
              src={mediaField.jsonBynderAsset.originalUrl + '?fm=webp&q=50'}
              alt={mediaField?.altText || 'alt text not found'}
              className="absolute inset-0 h-full w-full object-cover lg:rounded-xxl"
            />
          </div>
        )}
      <div
        className={`${overlay ? styles.gradient : ''
          } absolute inset-0 lg:rounded-b-xxl`}
      ></div>
      <div
        className={`absolute bottom-57 md:bottom-40 sm:bottom-20 px-7 lg:bottom-40 lg:px-12 ${isActive ? '' : 'animate-fadeOutDown'
          }`}
      >
        <h1 className="mb-3 font-title text-headingxl text-brand-yellow-1 lg:mb-2 ">
          {title}
        </h1>
        <p className="mb-5 text-bodym text-brand-white lg:mb-8 lg:text-bodyl">
          {text}
        </p>
        {(link?.slug || externalLink) && readMoreButtonTitle && (
          <LinkButton
            to={
              link
                ? generateCorrectSlug(
                  link,
                  prefix,
                  getUrlSubdirectory(
                    urlSubDirectorydictionary,
                    node_locale,
                    link?.__typename,
                  ),
                )
                : link && externalLink
                  ? generateCorrectSlug(
                    link,
                    prefix,
                    getUrlSubdirectory(
                      urlSubDirectorydictionary,
                      node_locale,
                      link?.__typename,
                    ),
                  )
                  : (externalLink as string)
            }
            variant="primary"
            isExternalLink={link ? false : link && externalLink ? false : true}
            openInNewTab={link ? false : link && externalLink ? false : true}
            size="regular"
            includeIosDoubleTapFix
            onClickHandler={handleCtaClick}
          >
            {readMoreButtonTitle}
          </LinkButton>
        )}
      </div>
    </>
  );
};

export default MainBannerSlideContent;
